import { useState, useContext, useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { DateTime } from 'luxon';
import apiTools from '../../services/api-tools';
import SpinnerLoader from '../layout/spinner-loader';
import { UserContext } from '../../contexts/user-context';
import { MdOutlineDelete, MdOutlineCompare } from 'react-icons/md';
import currency from 'currency.js';
import NetSheetComparison from './net-sheet-comparison';
import { EventTrack } from '../../services/utilities';
import RegionSelector from '../preferred-customer/region-selector';
import { DataGridPro} from '@mui/x-data-grid-pro';

const SavedNetSheets = () => {
    let { paramRegularOrReverse } = useParams();
    const { pcUser, activeRegion, logActivity } = useContext(UserContext);
    const [netSheets, setNetSheets] = useState([]);
    const [regularOrReverse, setRegularOrReverse] = useState("regular");
    const [selectedNetSheets, setSelectedNetSheets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletedNetSheet, setDeletedNetSheet] = useState(false);
    const [comparing, setComparing] = useState(false);
    const [compareError, setCompareError] = useState(null);

    const columns = [
        { field: 'lastSavedDate', headerName: 'Saved Date', flex: 1.4, valueFormatter: (value) => { return DateTime.fromISO(value).toFormat('MM/dd/yyyy'); } },
        { field: 'netSheetName', headerName: 'Name', flex: 4.0,
            renderCell: (params) => (
                <Link to={`${params.row.reverse ? '/reverse-' : '/'}net-sheet/${params.row.netSheetId}`}>
                    {params.value}
                </Link>
            ),
        },
        { field: 'closingDate', headerName: 'Closing Date', flex: 1.4, valueFormatter: (value) => { return DateTime.fromISO(value).toFormat('MM/dd/yyyy'); } },
        { field: 'purchasePrice', headerName: 'Purchase Price', flex: 1.6, valueFormatter: (value) => currency(value, { separator: ',', symbol: '$' }).format() },
        { field: 'select', headerName: 'Select', flex: 0.8, sortable: false, filterable: false,
            renderCell: (params) => (
                <input
                    type="checkbox"
                    checked={selectedNetSheets.some(ns => ns.netSheetId === params.row.netSheetId)}
                    onChange={() => addRemoveNetSheet(params.row)}
                />
            ),
        },
        { field: 'delete', headerName: 'Delete', flex: 0.8, sortable: false, filterable: false,
            renderCell: (params) => (
                <span onClick={() => deleteNetSheet(params.row.netSheetId)}>
                    <Link to=""><MdOutlineDelete /></Link>
                </span>
            ),
        },
    ];

    useEffect(() => {
        if (!paramRegularOrReverse) return;
        setRegularOrReverse(paramRegularOrReverse === 'reverse' ? 'reverse' : 'regular');
    }, [paramRegularOrReverse]);

    useEffect(() => {
        const getNetSheets = async () => {
            const response = await apiTools.get(`/net-sheets/${activeRegion.regionId}`);
            setNetSheets(response.data);
            setLoading(false);
        }
        if (pcUser && activeRegion) getNetSheets();

    }, [pcUser, activeRegion]);

    const filteredNetSheets = netSheets.filter(n =>
        regularOrReverse === 'reverse' ? n.reverse : !n.reverse
    );

    const rows = filteredNetSheets.map((netSheet, index) => ({
        id: netSheet.netSheetId || index,
        ...netSheet,
    }));
    
    useEffect(() => {
        const getNetSheets = async () => {
            setLoading(true);
            const response = await apiTools.get(`/net-sheets/${activeRegion.regionId}`);
            setNetSheets(response);
            setLoading(false);
            setDeletedNetSheet(false);
        }
        if (deletedNetSheet) getNetSheets();

    }, [deletedNetSheet]);

    useEffect(() => {
        setSelectedNetSheets([]);
    }, [regularOrReverse]);

    useEffect(() => {
        if (!comparing) setSelectedNetSheets([]);
    }, [comparing]);

    const deleteNetSheet = (netSheetId) => { 
        const del = async () => {
            await apiTools.post(`/net-sheet/delete/${netSheetId}`).then((response) => setDeletedNetSheet(true));
        }
        if (netSheetId) del();
        EventTrack("Net Sheet List", "Delete Saved", "Button");
    }

    const addRemoveNetSheet = (netSheet) => {
        setSelectedNetSheets((prev) => {
            const exists = prev.some(ns => ns.netSheetId === netSheet.netSheetId);
            let updated;

            if (exists) {
                updated = prev.filter(ns => ns.netSheetId !== netSheet.netSheetId);
            } else {
                updated = [...prev, netSheet];
            }

            return updated;
        });
    };

    const doCompare = () => {
        if (selectedNetSheets.length < 2) {
            setCompareError("You must select at least two net sheets to compare.")
        } else if (selectedNetSheets.length > 5) {
            setCompareError("You may only compare up to 5 net sheets at a time.")
        } else {
            setCompareError(null);
            setComparing(true);
            if (regularOrReverse === 'reverse') {
                logActivity("Reverse Netsheets Compared", ``);
                EventTrack("Net Sheet List", "Reverse Net Sheet Compared");
            } else {
                logActivity("Netsheets Compared", ``);
                EventTrack("Net Sheet List", "Net Sheet Compared");
            } 
        }
    }

    return (
        <>
            {!comparing && <><h1>Saved Net Sheets</h1>
                {regularOrReverse === 'regular' ? <Link to="/net-sheet" onClick={() => { EventTrack("Net Sheet List", "Net Sheet Started New"); }}>Start New Regular Net Sheet</Link> : <Link to="/reverse-net-sheet" onClick={() => { EventTrack("Net Sheet List", "Reverse Net Sheet Started New"); }}>Start New Reverse Net Sheet</Link>}
            <div>
                Below is a list of your {activeRegion && <>{activeRegion.regionName}</>} saved net sheets. <RegionSelector linkText="Find more counties" toolName="NetSheet" toolDisplayName="Saved Net Sheets" toolRoute="net-sheet-comparison" /> <br />
                Saved net sheets will be stored for one year.
            </div>
            <div className="row py-2">
                <div className="col">Net Sheet Type:</div>
                <div className="col">
                    <span className="form-control">
                        <label  className="me-2"><input type="radio" className="me-2" checked={regularOrReverse === 'regular'} onChange={() => setRegularOrReverse("regular")} />Regular </label>
                        <label><input type="radio" className="me-2" checked={regularOrReverse === 'reverse'} onChange={() => setRegularOrReverse("reverse")} />Reverse</label>
                    </span>
                </div>
            </div>
            <div className="row py-2">
                <div className="col"></div>
                    <div className="col">
                        <Link to="" className="btn btn-primary" onClick={() => doCompare()}><MdOutlineCompare  /> Compare</Link> 
                </div>
            </div>
            {compareError && <p className="text-danger">{compareError}</p>}

            {loading && <SpinnerLoader/>}
                {!loading && <div className="col-12">
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    autoHeight
                    disableRowSelectionOnClick
                        getRowId={(row) => row.netSheetId}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'lastSavedDate', sort: 'desc' }],
                            },
                        }}
                />
            </div>}
            <div className="row pt-1">
                <div className="col"></div>
                <div className="col">
                    <Link to="" className="btn btn-primary" onClick={() => doCompare()}><MdOutlineCompare /> Compare</Link> 
                </div>
                </div></>}
            {comparing && <NetSheetComparison netSheets={selectedNetSheets} setComparing={setComparing} reverse={regularOrReverse} />}
        </>
    );
}

export default SavedNetSheets;